/*!
 * liveLists.js — what the console's completion offers that has to be asked for
 * © 2026 Rob Canning — GPLv3
 *
 *     say("hi", voice:|          the voices installed (server/tts.js)
 *     text(cycle:"|              the project's text/ files (.txt, .csv)
 *     text(cycle:"names.csv", col:|   that file's columns, from its first row
 *
 * Each answers at once with what it has (nothing the first time) and asks
 * again in the background, so the list is there by the next key press.
 */

import { parseCsv } from "../cues/csvText.js";
import { fxBuses } from "../cues/audio/fxBus.js";
import { projectName } from "../system/projectContext.js";

const KEEP_MS = 15000;

// told when a list comes in changed (oscillaLive: the hint drawn again) --
// else the first cycle: of a session offered nothing until the next key
const listeners = new Set();
export const onListsChanged = fn => { listeners.add(fn); return () => listeners.delete(fn); };

/** A list kept for a while, fetched again after: get() -> its items now. */
function kept(load) {
  let items = [], at = -Infinity, busy = false;
  return () => {
    if (!busy && performance.now() - at > KEEP_MS) {
      busy = true;
      load().then(v => {
        const changed = JSON.stringify(v) !== JSON.stringify(items);
        items = v;
        if (changed) for (const fn of listeners) { try { fn(); } catch { /* a listener's own */ } }
      }, () => {}).finally(() => { at = performance.now(); busy = false; });
    }
    return items;
  };
}

/** The fx buses made so far (fxBus.js): bus:| */
export const buses = () => fxBuses().map(b => b.name);


let speakerCounts = {};             // voice -> how many speakers it has (those with more than one)

export const voices = kept(async () => {
  const r = await fetch("/api/tts/voices", { cache: "no-store" });
  const d = r.ok ? await r.json() : {};
  speakerCounts = d.speakers || {};
  // the names, and their short forms (lessac for en_US-lessac-medium)
  const out = [];
  for (const v of d.voices || []) { out.push(v); const short = v.split("-")[1]; if (short) out.push(short); }
  return [...new Set(out)];
});

export const textFiles = kept(async () => {
  const p = projectName();
  if (!p) return [];
  const r = await fetch(`/api/text-list/${encodeURIComponent(p)}`, { cache: "no-store" });
  // a README is about the folder, not words to show
  return r.ok ? ((await r.json()).files || []).filter(f => !/^readme\./i.test(f)) : [];
});

/** How many speakers the voice named (in full or in part: vctk) has; 1 for most. */
export function speakersOf(voice) {
  voices();
  const v = String(voice || "").toLowerCase();
  const hit = Object.keys(speakerCounts).find(k => k.toLowerCase() === v || k.toLowerCase().includes(v));
  return hit ? speakerCounts[hit] : 1;
}

const columns = new Map();          // file -> getter
/** The column names of a .csv in text/ (its first row). */
export function csvColumns(file) {
  if (!file) return [];
  if (!columns.has(file)) {
    columns.set(file, kept(async () => {
      const r = await fetch(`${window.textDir || `scores/${projectName()}/text/`}${file}`, { cache: "no-store" });
      if (!r.ok) return [];
      const rows = parseCsv((await r.text()).split(/\r?\n/).slice(0, 3).join("\n"));
      return (rows[0] || []).filter(Boolean);
    }));
  }
  return columns.get(file)();
}
